import { useThrottle } from '@core/shared/src/hooks/useThrottle';
import { ChatChannelIds, ChatMessage } from '@core/types';
import { ArrowDown, Info, Pause } from '@core/ui';
import { useAppDispatch } from '@online-webinar/store/src/hooks';
import { markChatChannelAsRead } from '@online-webinar/store/src/slices/live-webinar-slice';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemContent, Virtuoso, VirtuosoHandle } from 'react-virtuoso';

interface Props {
    channelId: string;
    messages: ChatMessage[];
    showOnDemandChatInfo?: boolean;
    classNames?: string;
    itemContent: ItemContent<any, any>;
}

export const ChatMessages = ({ channelId, messages, classNames = '', itemContent, showOnDemandChatInfo }: Props) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const throttledChatMessages = useThrottle(messages, channelId === ChatChannelIds.allChannel ? 1000 : 200);

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [followChat, setFollowChat] = useState(true);
    const [initialTopMostItemIndex, setInitialTopMostItemIndex] = useState(-1);
    const previousChannelId = useRef<string | null>(null);

    useEffect(() => {
        if (previousChannelId.current !== channelId) {
            previousChannelId.current = channelId;

            setChatMessages([]);
            setFollowChat(true);

            // Mark channel as read
            dispatch(markChatChannelAsRead(channelId));
        }
    }, [channelId]);

    useEffect(() => {
        setChatMessages(throttledChatMessages);

        if (followChat && virtuosoRef.current) {
            setInitialTopMostItemIndex(throttledChatMessages.length - 1);
            virtuosoRef.current.scrollToIndex({ index: throttledChatMessages.length - 1, align: 'end', behavior: 'smooth' });

            // Mark channel as read
            dispatch(markChatChannelAsRead(channelId));
        }
    }, [throttledChatMessages, followChat]);

    return (
        <div className={`relative flex flex-col ${classNames}`}>
            {showOnDemandChatInfo && (
                <div className="bg-info rounded-lg text-sm flex m-1 ps-1 pe-3 py-2">
                    <Info classNames="size-6 flex-none me-2 -mt-1" />
                    {t('Chat.OnDemand.InfoText')}
                </div>
            )}
            {!followChat && (
                <div className="absolute inset-x-0 bottom-0 bg-white/50 backdrop-blur-xs flex p-2 z-1">
                    <button
                        className="group bg-primary rounded-button cursor-pointer text-on-primary text-sm mx-auto overflow-hidden"
                        onClick={() => setFollowChat(true)}
                    >
                        <div className="grid justify-items-center group-hover:-translate-y-full group-focus-visible:-translate-y-full motion-safe:transition-transform motion-safe:duration-300 ease-in-out">
                            <span className="col-1 row-1 flex items-center px-3 py-2">
                                <Pause classNames="size-3.5 flex-none me-1 fill-current" />
                                {t('Chat.Button.ChatPaused.Paused')}
                            </span>
                            <span className="col-1 row-1 flex items-center px-3 py-2 translate-y-full">
                                <ArrowDown classNames="size-4 flex-none me-1 fill-current" />
                                {t('Chat.Button.ChatPaused.FollowChat')}
                            </span>
                        </div>
                    </button>
                </div>
            )}
            <Virtuoso
                ref={virtuosoRef}
                initialTopMostItemIndex={initialTopMostItemIndex}
                data={chatMessages}
                itemContent={itemContent}
                atBottomStateChange={(atBottom) => {
                    setFollowChat(atBottom);
                }}
                followOutput="auto"
                className="min-h-8 overflow-x-hidden overscroll-contain"
            />
        </div>
    );
};
